import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Zap, Target, Users, Star, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import PurchaseWaysSection from './PurchaseWaysSection';
import { motion } from 'framer-motion';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { gridContainerVariants, getGridItemVariants } from '@/lib/animations';
import { AnimatedGradientText } from '@/components/ui/animated-gradient-text';

// Local, optimized parallax utilities (mirrors Home.tsx)
type Axis = 'y' | 'x' | 'both';
interface ParallaxOptions {
  speed?: number;
  axis?: Axis;
  maxTranslate?: number;
  rootMargin?: string;
}
interface ParallaxItem {
  element: HTMLElement;
  speed: number;
  axis: Axis;
  maxTranslate: number;
  inView: boolean;
  lastTransform: string;
}
const parallaxItems = new Set<ParallaxItem>();
let parallaxIo: IntersectionObserver | null = null;
let parallaxRafId: number | null = null;
let parallaxRunning = false;
let prefersReducedMotion =
  typeof window !== 'undefined' &&
  !!window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function ensureParallaxIO(rootMargin: string) {
  if (parallaxIo) return;
  parallaxIo = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const item = [...parallaxItems].find((it) => it.element === entry.target);
        if (!item) continue;
        item.inView = entry.isIntersecting;
      }
      requestParallaxTick();
    },
    { root: null, threshold: 0, rootMargin },
  );
}
function onParallaxScroll() {
  requestParallaxTick();
}
function onParallaxResize() {
  requestParallaxTick();
}
function startParallax() {
  if (parallaxRunning) return;
  parallaxRunning = true;
  window.addEventListener('scroll', onParallaxScroll, { passive: true });
  window.addEventListener('resize', onParallaxResize, { passive: true });
  requestParallaxTick();
}
function stopParallax() {
  if (!parallaxRunning) return;
  window.removeEventListener('scroll', onParallaxScroll);
  window.removeEventListener('resize', onParallaxResize);
  parallaxRunning = false;
}
function requestParallaxTick() {
  if (parallaxRafId !== null) return;
  parallaxRafId = window.requestAnimationFrame(updateParallax);
}
function clamp(val: number, min: number, max: number) {
  return Math.min(Math.max(val, min), max);
}
function updateParallax() {
  parallaxRafId = null;
  if (prefersReducedMotion) {
    parallaxItems.forEach((item) => {
      if (item.lastTransform !== '') {
        item.element.style.transform = '';
        item.lastTransform = '';
      }
    });
    return;
  }
  const viewportHeight = window.innerHeight || 0;
  const viewportWidth = window.innerWidth || 0;
  const centerY = viewportHeight / 2;
  const centerX = viewportWidth / 2;
  const farTop = -viewportHeight * 1.5;
  const farBottom = viewportHeight * 2.5;
  parallaxItems.forEach((item) => {
    const rect = item.element.getBoundingClientRect();
    if (rect.bottom < farTop || rect.top > farBottom) return;
    const elementCenterY = rect.top + rect.height / 2;
    const elementCenterX = rect.left + rect.width / 2;
    const dy = elementCenterY - centerY;
    const dx = elementCenterX - centerX;
    const translateY = clamp(-dy * item.speed, -item.maxTranslate, item.maxTranslate);
    const translateX = clamp(-dx * item.speed, -item.maxTranslate, item.maxTranslate);
    let transform = '';
    if (item.axis === 'y') {
      transform = `translate3d(0, ${translateY.toFixed(2)}px, 0)`;
    } else if (item.axis === 'x') {
      transform = `translate3d(${translateX.toFixed(2)}px, 0, 0)`;
    } else {
      transform = `translate3d(${translateX.toFixed(2)}px, ${translateY.toFixed(2)}px, 0)`;
    }
    if (transform !== item.lastTransform) {
      item.element.style.transform = transform;
      item.lastTransform = transform;
    }
  });
}
function useParallaxRef(options: ParallaxOptions = {}) {
  const {
    speed = 0.06,
    axis = 'y',
    maxTranslate = 40,
    rootMargin = '20% 0px 20% 0px',
  } = options;
  const elRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = elRef.current as HTMLElement | null;
    if (!el) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => {
      prefersReducedMotion = mql.matches;
      requestParallaxTick();
    };
    mql.addEventListener?.('change', onChange);
    ensureParallaxIO(rootMargin);
    startParallax();
    const item: ParallaxItem = {
      element: el,
      speed,
      axis,
      maxTranslate,
      inView: false,
      lastTransform: '',
    };
    try {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 0;
      const vm = Math.max(0, Math.min(0.5, parseFloat(rootMargin.split(' ')[0]) / 100 || 0.2));
      const extendedTop = -vh * vm;
      const extendedBottom = vh * (1 + vm);
      item.inView = rect.bottom >= extendedTop && rect.top <= extendedBottom;
    } catch {
      item.inView = true;
    }
    parallaxItems.add(item);
    parallaxIo?.observe(el);
    el.style.willChange = 'transform';
    el.style.backfaceVisibility = 'hidden';
    el.style.transformStyle = 'preserve-3d';
    updateParallax();
    return () => {
      mql.removeEventListener?.('change', onChange);
      parallaxIo?.unobserve(el);
      parallaxItems.delete(item);
      el.style.willChange = '';
      el.style.backfaceVisibility = '';
      el.style.transformStyle = '';
      el.style.transform = '';
      if (parallaxItems.size === 0) {
        stopParallax();
      }
    };
  }, [axis, maxTranslate, rootMargin, speed]);
  return elRef;
}

const Info = () => {
  const whyBuyHereFeatures = [
    {
      title: 'Kickoff included',
      description:
        'a 60-minute call with the founder to sharpen positioning and a simple 90-day plan.',
    },
    {
      title: 'Action kit',
      description: 'launch checklist, landing outline, and next steps so you actually ship.',
    },
    {
      title: 'Direct answers, faster transfer',
      description: 'ask questions, decide, and complete via secure checkout with guidance.',
    },
    {
      title: 'Flexible deals',
      description: 'Buy Now (BIN) or Rent-to-Own.',
    },
    {
      title: 'Curation over clutter',
      description: 'only names chosen for clarity and intent—no endless trawling.',
    },
  ];

  const keywordBenefits = [
    {
      icon: Target,
      title: 'Clarity',
      description: 'the promise is obvious in the name—less explaining, more yes.',
    },
    {
      icon: Zap,
      title: 'Click-through',
      description: 'matches search and ad intent, improving CTR and lowering CAC.',
    },
    {
      icon: Users,
      title: 'Memory',
      description: 'easy to recall and repeat, which drives word of mouth.',
    },
  ];

  const bundleBenefits = [
    'More surfaces = more leads: multiple entry points for adjacent searches and campaigns.',
    'Topical strength: interlinking related sites helps content and rankings mature together.',
    'Operational efficiency: reuse the same assets, playbooks, and team across the set.',
    'Flexibility: keep, spin up, or resell individual names later.',
  ];

  const howItWorksSteps = [
    'Choose a domain (or bundle)',
    'Select Buy Now or Rent-to-Own',
    'Complete secure checkout with industry-standard encryption',
    'Get your booking link + action kit and ship your first version',
  ];

  const faqs = [
    {
      question: 'Can I keep my existing brand?',
      answer:
        'Yes. Use the keyword domain for campaigns or a category site and funnel to your brand.',
    },
    {
      question: 'Will you build the site for me?',
      answer: 'The kickoff gets you moving; full build help is optional.',
    },
    {
      question: 'Is there price parity with marketplaces?',
      answer:
        'Yes—same BIN. The extras (kickoff, action kit, guided transfer) are only when you buy here.',
    },
    {
      question: 'Any guarantees about rankings?',
      answer:
        'No promises—just best practices and a domain that earns clicks and recall from day one.',
    },
  ];

  // Subtle randomized, layered gradient background (same as Home)
  const backgroundStyle = useMemo<React.CSSProperties>(() => {
    const hue = Math.random() * (220 - 205) + 205;
    const saturation = Math.random() * (78 - 65) + 65;
    const lightNearWhite = Math.random() * (98 - 96) + 96;
    const lightBlue = Math.random() * (92 - 88) + 88;
    const alphaSoft1 = Math.random() * (0.1 - 0.06) + 0.06;
    const alphaSoft2 = Math.random() * (0.08 - 0.04) + 0.04;
    const centerX = Math.random() * (70 - 30) + 30;
    const centerY = Math.random() * (45 - 15) + 15;
    const verticalAngle = 180;
    const horizontalAngle = 90;
    const color2 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, 1)`;
    const tint1 = `hsla(${hue}, ${saturation}%, ${lightBlue}%, ${alphaSoft1})`;
    const tint2 = `hsla(${hue}, ${saturation}%, ${lightNearWhite}%, ${alphaSoft2})`;
    const backgroundImage = [
      `radial-gradient(ellipse at ${centerX}% ${centerY}%, ${tint1} 0%, transparent 60%)`,
      `linear-gradient(${verticalAngle}deg, #ffffff 0%, ${color2} 100%)`,
      `linear-gradient(${horizontalAngle}deg, ${tint2} 0%, transparent 50%, ${tint2} 100%)`,
    ].join(', ');
    return {
      backgroundImage,
      backgroundColor: '#ffffff',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover, cover, cover',
      backgroundAttachment: 'scroll',
    };
  }, []);

  // Parallax refs
  const heroTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 70 });
  const heroSubRef = useParallaxRef({ speed: -0.18, axis: 'y', maxTranslate: 64 });
  const heroCtaRef = useParallaxRef({ speed: -0.26, axis: 'y', maxTranslate: 108 });

  const whyKeywordTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 40 });
  const whyKeywordSubRef = useParallaxRef({ speed: -0.18, axis: 'y', maxTranslate: 64 });
  const whyKeywordGridRef = useParallaxRef({ speed: 0.08, axis: 'y', maxTranslate: 44 });

  const bundlesTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 36 });
  const bundlesSubRef = useParallaxRef({ speed: -0.18, axis: 'y', maxTranslate: 64 });
  const bundlesGridRef = useParallaxRef({ speed: 0.07, axis: 'y', maxTranslate: 40 });

  const marketTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 32 });
  const marketSubRef = useParallaxRef({ speed: -0.18, axis: 'y', maxTranslate: 64 });
  const marketGridRef = useParallaxRef({ speed: 0.07, axis: 'y', maxTranslate: 36 });

  const howTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 34 });
  const howGridRef = useParallaxRef({ speed: 0.07, axis: 'y', maxTranslate: 40 });

  const faqTitleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 32 });
  const faqGridRef = useParallaxRef({ speed: 0.07, axis: 'y', maxTranslate: 36 });

  const nextTitleRef = useParallaxRef({ speed: -0.16, axis: 'y', maxTranslate: 70 });

  // Use reveal once hooks to prevent cards from disappearing after scroll
  const whyKeywordReveal = useRevealOnce({ amount: 0.3 });
  const bundlesReveal = useRevealOnce({ amount: 0.3 });
  const marketReveal = useRevealOnce({ amount: 0.3 });
  const howReveal = useRevealOnce({ amount: 0.25 });
  const faqReveal = useRevealOnce({ amount: 0.25 });

  // Helper to merge parallax and reveal refs
  const mergeRefs = useCallback(
    (parallaxRef: React.RefObject<HTMLElement>, revealRef: React.RefObject<HTMLElement>) =>
      (node: HTMLElement | null) => {
        if (parallaxRef && 'current' in parallaxRef) {
          (parallaxRef as React.MutableRefObject<HTMLElement | null>).current = node;
        }
        if (revealRef && 'current' in revealRef) {
          revealRef.current = node;
        }
      },
    [],
  );

  return (
    <>
      <Helmet>
        <title>Why Buy on Attain Domains | Domain Marketplace Guide</title>
        <meta
          name="description"
          content="Learn why keyword domains work better than invented brands. Get expert guidance, action kits, and secure transfers when you buy direct from Attain Domains."
        />
      </Helmet>

      <div className="flex flex-col min-h-screen" style={backgroundStyle}>
        {/* Hero Section */}
        <section className="relative py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto text-center stagger-fade-1">
              <h1
                ref={heroTitleRef as any}
                className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg"
              >
                Why Buy on{' '}
                <Link to="/services" className="inline-block hover:opacity-90 transition-opacity">
                  <AnimatedGradientText colorFrom="#0000FF" colorTo="#000000" className="text-6xl md:text-7xl font-bold">
                    Attain
                  </AnimatedGradientText>
                </Link>{' '}
                <Link to="/domains" className="inline-block hover:opacity-90 transition-opacity">
                  <AnimatedGradientText colorFrom="#0000FF" colorTo="#000000" className="text-6xl md:text-7xl font-bold">
                    Domains
                  </AnimatedGradientText>
                </Link>
              </h1>
              <p
                ref={heroSubRef as any}
                className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed mb-12 font-normal"
              >
                Fast transfers, pay later, and expert help.
              </p>

              <div ref={heroCtaRef as any} className="flex justify-center mb-8">
                <Link to="/bundles">
                  <Button
                    size="lg"
                    className="apple-button text-lg px-12 py-4 h-auto text-primary hover:text-white"
                  >
                    Browse Domains
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* What Attain Domains Is */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="apple-card p-12 text-center">
                <h2 className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg">
                  What Attain Domains Is (and who it's for)
                </h2>
                <p className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed font-normal">
                  Attain Domains is a curated set of keyword-focused domains and bundles for
                  founders, agencies, and operators who want to launch faster and convert
                  better—without guessing whether the name will work.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why keyword domains work */}
        <section className="py-24 apple-section-bg">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16 stagger-fade-1">
                <h2
                  ref={whyKeywordTitleRef as any}
                  className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg"
                >
                  Why keyword domains work
                </h2>
                <p
                  ref={whyKeywordSubRef as any}
                  className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed mb-12 font-normal"
                >
                  A good domain does three jobs from day one:
                </p>
              </div>

              <motion.div
                ref={mergeRefs(whyKeywordGridRef as any, whyKeywordReveal.ref)}
                className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12"
                variants={gridContainerVariants}
                initial="hidden"
                animate={whyKeywordReveal.controls}
              >
                {keywordBenefits.map((benefit, index) => (
                  <motion.div key={index} variants={getGridItemVariants(index)} className="apple-card p-8 text-center group">
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <benefit.icon className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4 text-black">{benefit.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{benefit.description}</p>
                  </motion.div>
                ))}
              </motion.div>

              <div className="apple-card p-8 text-center bg-primary/5 border-primary/20">
                <p className="text-lg text-foreground font-medium">
                  If you plan to run SEO, ads, partnerships, or outbound, your domain is the first
                  line of copy everyone sees. <strong>Make it carry weight.</strong>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why bundles */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2
                  ref={bundlesTitleRef as any}
                  className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg"
                >
                  Why bundles (when to choose one)
                </h2>
                <p
                  ref={bundlesSubRef as any}
                  className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed mb-8 font-normal"
                >
                  Pick a bundle when you want coverage across a category, not just one URL.
                </p>
              </div>

              <motion.div
                ref={mergeRefs(bundlesGridRef as any, bundlesReveal.ref)}
                className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8"
                variants={gridContainerVariants}
                initial="hidden"
                animate={bundlesReveal.controls}
              >
                <motion.div variants={getGridItemVariants(0)} className="apple-card p-8">
                  <h3 className="text-2xl font-bold mb-6 text-black">Bundle Benefits</h3>
                  <ul className="space-y-4">
                    {bundleBenefits.map((benefit, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span className="text-muted-foreground">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>

                <motion.div variants={getGridItemVariants(1)} className="apple-card p-8 bg-primary/5 border-primary/20">
                  <h3 className="text-2xl font-bold mb-6 text-black">Decision Guide</h3>
                  <div className="space-y-4">
                    <p className="text-muted-foreground">
                      <strong className="text-foreground">Choose single domains</strong> when you
                      have one offer and one funnel.
                    </p>
                    <p className="text-muted-foreground">
                      <strong className="text-foreground">Choose bundles</strong> to own a topic and
                      route demand.
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Why buy here instead of a marketplace */}
        <section className="py-24 apple-section-bg">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2
                  ref={marketTitleRef as any}
                  className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg"
                >
                  Why buy here instead of a marketplace
                </h2>
                <p
                  ref={marketSubRef as any}
                  className="text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed mb-8 font-normal"
                >
                  Same market pricing; more value and speed.
                </p>
              </div>

              <motion.div
                ref={mergeRefs(marketGridRef as any, marketReveal.ref)}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12"
                variants={gridContainerVariants}
                initial="hidden"
                animate={marketReveal.controls}
              >
                {whyBuyHereFeatures.map((feature, index) => (
                  <motion.div key={index} variants={getGridItemVariants(index)} className="apple-card p-6">
                    <h3 className="text-xl font-bold mb-3 text-black">{feature.title}:</h3>
                    <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                  </motion.div>
                ))}
              </motion.div>

              <div className="apple-card p-8 text-center bg-primary/5 border-primary/20">
                <p className="text-xl font-bold text-foreground">
                  Bottom line: Marketplaces sell the listing. Here, you get the name + a plan.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Purchase Ways */}
        <PurchaseWaysSection variant="info" />

        {/* How buying works */}
        <section className="py-24 apple-section-bg">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2
                  ref={howTitleRef as any}
                  className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg"
                >
                  How buying works (simple)
                </h2>
              </div>

              <motion.div
                ref={mergeRefs(howGridRef as any, howReveal.ref)}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
                variants={gridContainerVariants}
                initial="hidden"
                animate={howReveal.controls}
              >
                {howItWorksSteps.map((step, index) => (
                  <motion.div key={index} variants={getGridItemVariants(index)} className="apple-card p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xl">
                      {index + 1}
                    </div>
                    <p className="text-muted-foreground">{step}</p>
                  </motion.div>
                ))}
              </motion.div>

              <div className="apple-card p-6 bg-muted/50">
                <p className="text-sm text-muted-foreground text-center">
                  <strong>Kickoff details:</strong> One 60-minute session per domain. Schedule
                  within 30 days. RTO unlocks the consult after your first payment clears.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Quick answers */}
        <section className="py-24 apple-section-bg">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2
                  ref={faqTitleRef as any}
                  className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg"
                >
                  Quick answers
                </h2>
              </div>

              <motion.div
                ref={mergeRefs(faqGridRef as any, faqReveal.ref)}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
                variants={gridContainerVariants}
                initial="hidden"
                animate={faqReveal.controls}
              >
                {faqs.map((faq, index) => (
                  <motion.div key={index} variants={getGridItemVariants(index)} className="apple-card p-6">
                    <h3 className="text-lg font-bold mb-3 text-black">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* What to do next */}
        <section className="py-24 apple-section-bg">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2
                  ref={nextTitleRef as any}
                  className="text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg"
                >
                  What to do next
                </h2>
              </div>

              <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
                <Link to="/bundles">
                  <Button size="lg" className="apple-button text-lg px-12 py-4 h-auto">
                    Browse Domains
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>

                <Link to="/faqs">
                  <Button
                    size="lg"
                    variant="outline"
                    className="apple-button-secondary text-lg px-12 py-4 h-auto"
                  >
                    Read FAQs
                  </Button>
                </Link>
              </div>

              <div className="apple-card p-8 text-center bg-primary/5 border-primary/20">
                <p className="text-lg font-medium text-foreground">
                  <strong>Final note:</strong> Good names don't sit. If one fits your plan, secure
                  it and we'll map your first steps together.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Info;
